import { useMemo, type ComponentProps } from "react";
import * as AvatarPrimitive from "@radix-ui/react-avatar";
import { type ColorMode, getDeterministicHexColor, getAvatarBackground } from "@tellescope/utils";

import { cn } from "../../lib/utils";
import { avatarFallbackVariants, type AvatarSize } from "./styles";

export interface AvatarFallbackConfigProps {
  /** Seeds the deterministic fallback background/color. */
  id: string;
  /**
   * Renders a deterministic glass-texture background (from `id`).
   * @default true
   */
  enableStaticBackground?: boolean;
  /**
   * When `enableStaticBackground` is off, tints the flat fallback from `id` instead of `bg-muted`.
   * @default false
   */
  enableDeterministicColor?: boolean;
  /** @default enableDeterministicColor ? "dark" : undefined */
  deterministicColorMode?: ColorMode;
}

export interface AvatarFallbackProps
  extends Omit<ComponentProps<typeof AvatarPrimitive.Fallback>, keyof AvatarFallbackConfigProps>,
    AvatarFallbackConfigProps {
  size?: AvatarSize;
}

export const AvatarFallback = ({
  className,
  enableDeterministicColor = false,
  enableStaticBackground = true,
  id,
  size,
  deterministicColorMode = enableDeterministicColor ? "dark" : undefined,
  ...props
}: AvatarFallbackProps) => {
  const backgroundColor = useMemo(
    () => getDeterministicHexColor(id, { mode: deterministicColorMode }),
    [deterministicColorMode, id]
  );

  const texturedBackground = useMemo(
    () => (enableStaticBackground ? getAvatarBackground(id) : undefined),
    [enableStaticBackground, id]
  );

  if (texturedBackground) {
    return (
      <AvatarPrimitive.Fallback
        data-slot="avatar-fallback"
        className={cn(avatarFallbackVariants({ size }), "text-white", className)}
        style={{ backgroundImage: `url(${texturedBackground.svgDataUrl})`, backgroundSize: "cover" }}
        {...props}
      />
    );
  }

  return (
    <AvatarPrimitive.Fallback
      data-slot="avatar-fallback"
      style={{
        "--background-color": backgroundColor,
        "--text-color": deterministicColorMode === "dark" ? "white" : "black",
      }}
      className={cn(
        avatarFallbackVariants({ size }),
        "bg-muted",
        enableDeterministicColor && "bg-[var(--background-color)] text-[var(--text-color)]",
        className
      )}
      {...props}
    />
  );
};
